import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { Home, Users, Briefcase, BarChart3, CreditCard, Building2, User, LogOut, Plus, Wallet, HelpCircle, FileText, Share2, ArrowUpCircle, Settings, Sun, Moon, Bell, ChevronLeft, ChevronRight } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { useTheme } from "next-themes";

const links = {
  broker_admin: [
    { to: "/app", label: "Home", icon: Home, end: true },
    { to: "/app/agreement-builder", label: "Create Agreement", icon: Plus },
    { to: "/app/team", label: "My Team", icon: Users },
    { to: "/app/deals", label: "All Deals", icon: Briefcase },
    { to: "/app/earnings", label: "My Earnings", icon: Wallet },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, comingSoon: true },
    { to: "/app/plan", label: "Plan & Billing", icon: CreditCard },
    { to: "/app/business", label: "Business Profile", icon: Building2 },
    { to: "/app/referral", label: "Referral", icon: Share2 },
    { to: "/app/profile", label: "Profile", icon: User },
    { to: "/app/settings", label: "Settings", icon: Settings },
  ],
  agent: [
    { to: "/app", label: "Home", icon: Home, end: true },
    { to: "/app/deals", label: "My Deals", icon: Briefcase },
    { to: "/app/agreement-builder", label: "Create Agreement", icon: Plus },
    { to: "/app/earnings", label: "My Earnings", icon: Wallet },
    { to: "/app/plan", label: "Plan & Billing", icon: CreditCard },
    { to: "/app/referral", label: "Referral", icon: Share2 },
    { to: "/app/profile", label: "Profile", icon: User },
    { to: "/app/settings", label: "Settings", icon: Settings },
  ],
  landlord: [
    { to: "/app", label: "Overview", icon: Home, end: true },
    { to: "/app/deposits", label: "Deposits", icon: FileText },
    { to: "/app/wallet", label: "Wallet", icon: Wallet },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/settings", label: "Settings", icon: Settings },
    { to: "/app/support", label: "Support", icon: HelpCircle },
  ],
  landlord_self_managed: [
    { to: "/app", label: "Overview", icon: Home, end: true },
    { to: "/app/agreement-builder", label: "Create Agreement", icon: Plus },
    { to: "/app/deposits", label: "Deposits", icon: FileText },
    { to: "/app/wallet", label: "Wallet", icon: Wallet },
    { to: "/app/notifications", label: "Notifications", icon: Bell },
    { to: "/app/settings", label: "Settings", icon: Settings },
    { to: "/app/support", label: "Support", icon: HelpCircle },
  ],
  tenant: [
    { to: "/app", label: "Home", icon: Home, end: true },
    { to: "/app/deals", label: "My Rental", icon: FileText },
    { to: "/app/tenant-wallet", label: "Wallet", icon: Wallet },
    { to: "/app/profile", label: "Profile", icon: User },
    { to: "/app/help", label: "Help", icon: HelpCircle },
    { to: "/app/settings", label: "Settings", icon: Settings },
  ],
};

const roleLabel = { broker_admin: "Broker", agent: "Agent", tenant: "Tenant", landlord: "Landlord" };

export default function Sidebar({ onClose, collapsed = false, onToggleCollapse }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { lang, setLang, t } = useI18n();
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const roleKey = user?.role === "landlord" && user?.self_managed ? "landlord_self_managed" : user?.role;
  const items = links[roleKey] || [];

  // Map sidebar labels to translation keys
  const getTranslatedLabel = (label) => {
    const labelMap = {
      "Home": "sidebar_home",
      "Overview": "sidebar_overview",
      "Create Agreement": "sidebar_create_agreement",
      "My Team": "sidebar_my_team",
      "My Deals": "sidebar_my_deals",
      "All Deals": "sidebar_all_deals",
      "My Earnings": "sidebar_my_earnings",
      "My Rental": "sidebar_my_rental",
      "O-Fix": "sidebar_ofix",
      "Deposits": "sidebar_deposits",
      "Wallet": "sidebar_wallet",
      "Notifications": "sidebar_notifications",
      "Analytics": "sidebar_analytics",
      "Business Profile": "sidebar_business_profile",
      "Referral": "sidebar_referral",
      "Profile": "sidebar_profile",
      "Settings": "sidebar_settings",
      "Support": "sidebar_support",
      "Help": "sidebar_help",
    };
    return t(labelMap[label] || label);
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  const sidebarWidth = collapsed ? 80 : 240;

  return (
    <aside
      data-testid="sidebar"
      style={{
        width: sidebarWidth, flexShrink: 0, minHeight: "100vh",
        background: "var(--dash-sidebar)",
        borderRight: "1px solid var(--dash-border)",
        display: "flex", flexDirection: "column",
        transition: "width 0.3s ease",
        position: "relative",
      }}
    >
      {/* Logo + user */}
      <div style={{ padding: "20px", borderBottom: "1px solid var(--dash-border)", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          {!collapsed && <Logo size="sm" />}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              style={{
                padding: 6,
                borderRadius: 6,
                background: "var(--dash-hover)",
                border: "1px solid var(--dash-border)",
                color: "var(--dash-text-2)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => (e.target.style.background = "var(--dash-border)")}
              onMouseLeave={(e) => (e.target.style.background = "var(--dash-hover)")}
            >
              {collapsed ? <ChevronRight style={{ height: 16, width: 16 }} /> : <ChevronLeft style={{ height: 16, width: 16 }} />}
            </button>
          )}
        </div>

        {/* Create New Agreements button for self-managed landlords */}
        {user?.role === "landlord" && user?.self_managed && (
          <Link
            to="/app/new-agreement"
            title={collapsed ? "New Agreement" : ""}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 8,
              padding: "10px 12px",
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              color: "#fff",
              textDecoration: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.target.style.opacity = "1")}
          >
            <Plus style={{ height: 14, width: 14 }} />
            {!collapsed && "New Agreement"}
          </Link>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: collapsed ? 0 : 8, justifyContent: collapsed ? "center" : "flex-start" }}>
          <div style={{ height: 32, width: 32, borderRadius: "50%", background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 14, flexShrink: 0 }} title={user?.name}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          {!collapsed && (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--dash-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>
              <span style={{ fontSize: 10, fontWeight: 500, background: "rgba(249,115,22,0.12)", color: "#F97316", padding: "2px 8px", borderRadius: 999 }}>{roleLabel[user?.role]}</span>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((it) => {
          if (it.comingSoon) {
            return (
              <div key={it.to}
                title={collapsed ? it.label : ""}
                style={{
                  display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", gap: 10,
                  padding: "9px 12px", borderRadius: 8, fontSize: 13,
                  fontWeight: 400,
                  textDecoration: "none",
                  color: "var(--dash-text-3)",
                  background: "transparent",
                  opacity: 0.5,
                  cursor: "not-allowed",
                  position: "relative",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <it.icon style={{ height: 15, width: 15, flexShrink: 0 }} />
                  {!collapsed && getTranslatedLabel(it.label)}
                </div>
                {!collapsed && <span style={{ fontSize: 10, fontWeight: 600, background: "rgba(249,115,22,0.12)", color: "#F97316", padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>Coming Soon</span>}
              </div>
            );
          }
          return (
            <NavLink key={it.to} to={it.to} end={it.end}
              onClick={handleNavClick}
              title={collapsed ? getTranslatedLabel(it.label) : ""}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10,
                padding: "9px 12px", borderRadius: 8, fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                textDecoration: "none",
                color: isActive ? "#F97316" : "var(--dash-text-2)",
                background: isActive ? "linear-gradient(135deg, rgba(249,115,22,0.15), rgba(139,92,246,0.15))" : "transparent",
                borderLeft: collapsed ? "none" : (isActive ? "3px solid #F97316" : "3px solid transparent"),
                borderRadius: collapsed ? 8 : 8,
                position: "relative",
                transition: "all 0.2s ease",
              })}
            >
              <it.icon style={{ height: 15, width: 15, flexShrink: 0 }} />
              {!collapsed && getTranslatedLabel(it.label)}
            </NavLink>
          );
        })}

        {user?.role === "agent" && (
          <NavLink to="/upgrade-to-broker" title={collapsed ? "Upgrade to Broker" : ""} style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 10, padding: "9px 12px", borderRadius: 8, fontSize: 13, color: "#F97316", textDecoration: "none", border: "1px solid rgba(249,115,22,0.2)", marginTop: 4, transition: "all 0.2s ease" }}>
            <ArrowUpCircle style={{ height: 15, width: 15, flexShrink: 0 }} />
            {!collapsed && "Upgrade to Broker"}
          </NavLink>
        )}
      </nav>

      {/* Bottom controls */}
      <div style={{ padding: 12, borderTop: "1px solid var(--dash-border)", display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Theme toggle */}
        <button
          title={collapsed ? (isDark ? "Light mode" : "Dark mode") : ""}
          onClick={() => setTheme(isDark ? "light" : "dark")}
          style={{
            display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 8, padding: "8px 12px",
            borderRadius: 8, fontSize: 13, cursor: "pointer", width: "100%",
            background: "var(--dash-hover)", border: "1px solid var(--dash-border)",
            color: "var(--dash-text-2)", transition: "all 0.2s ease",
          }}
        >
          {isDark ? <Sun style={{ height: 14, width: 14, color: "#F97316", flexShrink: 0 }} /> : <Moon style={{ height: 14, width: 14, color: "#F97316", flexShrink: 0 }} />}
          {!collapsed && (isDark ? "Light mode" : "Dark mode")}
        </button>

        {!collapsed && (
          <select
            data-testid="lang-switcher-sidebar"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            style={{ width: "100%", background: "var(--dash-input)", color: "var(--dash-text)", fontSize: 13, borderRadius: 8, padding: "8px 12px", border: "1px solid var(--dash-border)", outline: "none" }}
          >
            <option value="en">English</option>
            <option value="hi">हिंदी</option>
            <option value="mr">मराठी</option>
            <option value="kn">ಕನ್ನಡ</option>
            <option value="gu">ગુજરાતી</option>
          </select>
        )}

        <button
          title={collapsed ? "Sign out" : ""}
          data-testid="logout-btn"
          onClick={async () => { await logout(); nav("/"); }}
          style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", gap: 8, padding: "8px 12px", borderRadius: 8, fontSize: 13, color: "#ef4444", background: "transparent", border: "none", cursor: "pointer", width: "100%", transition: "all 0.2s ease" }}
        >
          <LogOut style={{ height: 14, width: 14, flexShrink: 0 }} /> {!collapsed && "Sign out"}
        </button>
      </div>
    </aside>
  );
}
